import React from 'react';
import fitAngledAxisLabel, { LABEL_DY_PX } from '../../helpers/fitAngledAxisLabel';

/**
 * Recharts x-axis tick that renders the label at an angle.
 *
 * Long category / measure-moment names (e.g. "Vooraanmelding",
 * "Voormeting (1)") stay readable and don't overlap on narrow cards.
 * Recharts injects `x`, `y` and `payload`; pass `fill` from the chart's
 * `graphColor`. Font sizing comes from the `.angled-axis-tick` CSS class
 * (a class beats the global `text { font-size: 30px }` rule; inline styles
 * are disallowed by project convention).
 *
 * A custom tick element bypasses the XAxis `tickFormatter`, so charts that
 * format their raw axis value (e.g. a "YYYY-MM-DD" day key shown as "dd/mm")
 * must pass that same formatter here via the `formatter` prop.
 *
 * The label is shortened to fit the strip the axis reserves (`height`, injected
 * by Recharts): a longer label pokes out below the axis and runs over the
 * legend. The full text stays available on hover via `<title>`.
 * AI-CONTEXT: See src/helpers/fitAngledAxisLabel.js
 *
 * @param {object} props
 * @param {number} [props.x] - Tick x-coordinate (injected by Recharts).
 * @param {number} [props.y] - Tick y-coordinate (injected by Recharts).
 * @param {{ value?: string|number }} [props.payload] - Tick payload (injected by Recharts).
 * @param {string} [props.fill] - Text colour; defaults to black.
 * @param {(value: string|number) => string} [props.formatter] - Optional formatter applied to the raw value (mirrors XAxis `tickFormatter`).
 * @param {number} [props.height] - Height of the XAxis strip (injected by Recharts).
 * @returns {JSX.Element}
 */
const AngledAxisTick = ({ x, y, payload, fill, formatter, height }) => {
  const raw = payload?.value;
  const label = formatter ? formatter(raw) : raw;
  const { text, truncated } = fitAngledAxisLabel(label, height);
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        className="angled-axis-tick"
        x={0}
        y={0}
        dy={LABEL_DY_PX}
        textAnchor="end"
        transform="rotate(-35)"
        fill={fill || 'black'}
      >
        {truncated && <title>{String(label)}</title>}
        {text}
      </text>
    </g>
  );
};

export default AngledAxisTick;
